import Bluebird from 'bluebird'
import { useCallback, useRef, useState } from 'react'
import { useSelectedVariantContext } from '../components/step2/context/SelectedVariantContext'
import {
    actionChangeDisplay,
    actionSaveCacheCapture,
    actionUpdatePreviewImages,
    actionCaptureBackground, actionSetRunningImageCombine
} from '../components/step2/context/SelectedVariantContext/action'
import {
    generateImagePreviewUrl,
    getMockupConfig,
    handleCaptureArtwork3D,
    handleCaptures,
    handleGenerateDesign, handleGenerateDesignNoTemplate, handleMergeDesignCaptures,
    handleUploadDesign,
    mergeAttributesByDesign,
    mergeDesignCapturesLocal
} from '../components/step2/context/SelectedVariantContext/helper'
import { getLinksUploadArtwork } from '../services/ArtworkTemplateServices'
import { toaster } from '../shared/PFToast'
import captureUpload from '../webworkers/helpers/captureUpload'
import createDebug from "debug";
import {getLocalData, setLocalData} from '../services/StorageServices'
import { DESIGN_MODES } from '../components/step2/Step2Container'

export function usePreviewArtworks() {
    const {
        state: {pickerAttributes, selectedAttribute, designs, reloadPreview, productCreatedType, designsCaptures, designMode},
        dispatch
    } = useSelectedVariantContext()

    const [pending, setPending] = useState(false)
    const [percentage, setPercentage] = useState(0)

    const compositeCanvasRef = useRef(null)

    const fetchAreas = useCallback(
        async (options = {}) => {
            setPending(true)
            const {fetchDesign} = options

            const captures = await handleCaptures(
                selectedAttribute,
                fetchDesign
                    ? {isSafeZone: true}
                    : {isArea: true, scalePreviewRatio: selectedAttribute.scalePreviewRatio}
            ).then((res) => {
                return res
            })
            const artworks = captures.map((capture) => capture.area).filter(item => item.side !== 'all-in-one')

            const result = {mockup: selectedAttribute.mockup, artworks}

            if (fetchDesign) {
                const {design, config} = await handleGenerateDesign(selectedAttribute.mockup, artworks, {
                    scale: selectedAttribute.scalePreviewRatio
                })
                result.design = design
                result.config = config
            }

            return result
        },
        [selectedAttribute]
    )

    const fetchPreviews = useCallback(async () => {
        try {
            if (reloadPreview) {
                setPercentage(0)
                setPending(true)
                const startTime = performance.now()
                const intervalId = setInterval(() => {
                    const elapsedTime = performance.now() - startTime
                    const progress = Math.round((elapsedTime / 30000) * 100) // Assuming the promise will take 30 seconds
                    setPercentage(progress)

                    if (progress >= 100) {
                        clearInterval(intervalId)
                    }
                }, 100)

                const attrsData = mergeAttributesByDesign(pickerAttributes, designs)

                const fetchedPreviews = await Bluebird.mapSeries(attrsData, async (attributes) => {
                    const attribute = attributes[0]


                    const captures = await handleCaptures(attribute, {
                        isPreview: true,
                        scalePreviewRatio: attribute.scalePreviewRatio
                    }).then((res) => res)
                    const artworks = captures.map((capture) => capture.artwork)

                    dispatch(
                        actionSaveCacheCapture({
                            attributes,
                            artworks: artworks,
                            type: 'preview'
                        })
                    )




                    const {design, config} = await handleGenerateDesign(attribute.mockup, artworks, {
                        ratio: attribute.scalePreviewRatio
                    })

                    const arrAttributes = []
                    const listSku = []
                    for (const item of attributes) {
                        arrAttributes.push(item.attributes.map((attr) => ({type: attr.type, value: attr.value_code})))
                        listSku.push(...item.listSku)
                    }

                    return {
                        attributes: arrAttributes,
                        attribute,
                        listSku,
                        design,
                        config
                    }
                })
                
                const totalPreview = fetchedPreviews.length
                const {data} = await getLinksUploadArtwork({limit: totalPreview})

                const presignedUrls = data || []
                if (presignedUrls.length !== totalPreview) throw new Error('Failed to get presigned url')

                const previewImages = await Bluebird.map(fetchedPreviews, async (preview, index) => {
                    const {attributes, listSku, design, config, attribute} = preview
                    const presignedUrl = presignedUrls[index]
                    const payload = {
                        design,
                        config,
                        presignedUrl
                    }
                    const {data, success, message} = await handleUploadDesign(payload)
                    if (!success) throw new Error(message)

                    const images = generateImagePreviewUrl(data, attribute)

                    return {
                        images,
                        attributes,
                        listSku
                    }
                })

                clearInterval(intervalId)
                setPercentage(100)
                setTimeout(() => {
                    setPending(false)
                    dispatch(actionUpdatePreviewImages(previewImages))
                }, 300)
            }
        } catch (error) {
            toaster({
                type: 'error',
                message: `Overload: ${error.message}`
            })
            setPending(false)
            dispatch(actionChangeDisplay('design'))
        }
    }, [pickerAttributes, dispatch, designs, reloadPreview])

    const fetchDesigns = useCallback(async () => {
        try {
            setPending(true)

            const attrsData = mergeAttributesByDesign(pickerAttributes, designs)


            const fetchedDesigns = await Bluebird.mapSeries(attrsData, async (attributes) => {
                const attribute = attributes[0]
                const captures = await handleCaptures(attribute, {isPublish: true}).then((res) => res)
                const artworks = captures.map((capture) => capture.artwork)

                dispatch(
                    actionSaveCacheCapture({
                        attributes,
                        artworks: artworks,
                        type: 'publish'
                    })
                )

                const {design, areas} = productCreatedType === 'regularNoTemplate' ? await handleGenerateDesignNoTemplate(attribute.mockup, artworks) : await handleGenerateDesign(attribute.mockup, artworks)

                const arrAttributes = []
                const listSku = []

                for (const item of attributes) {
                    arrAttributes.push(item.attributes.map((attr) => ({type: attr.type, value: attr.value_code})))
                    listSku.push(...item.listSku)
                }

                return productCreatedType === 'regularNoTemplate' ? {
                    design,
                    listSku,
                    level: attribute.level || 'variant'
                } : {
                    design,
                    areas,
                    blob: URL.createObjectURL(design),
                    mockup: attribute.mockup,
                    listSku,
                    level: attribute.level || 'variant'
                }
            })
            
            return fetchedDesigns
        } catch (error) {
            toaster({
                type: 'error',
                message: `Overload: ${error.message}`
            })
        } finally {
            setPending(false)
        }
    }, [pickerAttributes, designs, dispatch])

    const fetchDesignsNoCapture = useCallback(async () => {
        try {
            setPending(true)

            const attrsData = mergeAttributesByDesign(pickerAttributes, designs)

            const fetchedDesigns = await Bluebird.mapSeries(attrsData, async (attributes) => {
                const attribute = attributes[0]

                const listSku = []

                for (const item of attributes) {
                    listSku.push(...item.listSku)
                }

                return {
                    mockup: attribute.mockup,
                    listSku,
                    level: attribute.level || 'variant'
                }
            })

            return fetchedDesigns
        } catch (error) {
            toaster({
                type: 'error',
                message: `Overload: ${error.message}`
            })
        } finally {
            setPending(false)
        }
    }, [pickerAttributes, designs, dispatch])

    const fetchPreview3D = useCallback((composite = false, scale = 0.5) => {
        try {
            const start = Date.now()

            const attribute = pickerAttributes.find((attr) => attr._id === selectedAttribute._id)
            if (!attribute) return null

            const captures = Object.keys(attribute.ref).filter((key) => {
                return designMode === DESIGN_MODES.COMBINED ? key === 'all-in-one' : key !== 'all-in-one'
            }
            ).map((key) => {
                const stageRef = attribute.ref[key]
                const safeZone = attribute.safeZone[key]

                const canvas = handleCaptureArtwork3D({ stageRef, safeZone })

                return {
                    side: key,
                    canvas: canvas,
                }
            })

            if (composite) {
                const config = getMockupConfig(attribute.mockup)

                if (!compositeCanvasRef.current) {
                    compositeCanvasRef.current = new OffscreenCanvas(config.width * scale, config.height * scale);
                } else {
                    compositeCanvasRef.current.width = config.width * scale;
                    compositeCanvasRef.current.height = config.height * scale;
                }

                const ctx = compositeCanvasRef.current.getContext('2d')
                
                captures.forEach((capture) => {
                    const currentPart = config.part_configs[capture.side]
                    ctx.drawImage(capture.canvas, currentPart.left * scale, currentPart.top * scale, currentPart.width * scale, currentPart.height * scale)
                    const captureCtx = capture.canvas.getContext('2d')
                    captureCtx?.clearRect(0, 0, capture.canvas.width, capture.canvas.height)
                })

                return compositeCanvasRef.current
            }

            return captures
        } catch (error) {
            console.error(error)
        }
    }, [pickerAttributes, selectedAttribute, dispatch, designMode])

    const fetchCapturedSides = useCallback(async (inputData = null, isNext = false) => {
        const debug = createDebug("pf126:PLC-12476");
        try {
            if (inputData.length > 0) {
                debug("capture start", inputData);
                const fetchedCapturedSides = await Bluebird.mapSeries(inputData, async (attributes) => {
                    const attribute = Array.isArray(attributes) ? attributes[0] : attributes

                    // Capture all sides
                    const captures = await handleCaptures(attribute, {isPublish: true})
                    const artworks = captures.map((capture) => capture.artwork)

                    // Process individual sides for upload
                    const individualSides = await processIndividualSides(artworks, attribute)

                    // Transform to output-capture-common format
                    const sideObject = {}
                    individualSides.forEach(sideData => {
                        sideObject[sideData.side] = sideData.url
                    })

                    return {
                        images: sideObject,
                        _id: attribute._id,
                        preview_v2: attribute.mockup.preview_v2,
                        side_infos: attribute.mockup.side_infos,
                        preview_meta: attribute.mockup.preview_meta
                    }
                })
                const result = await mergeDesignCapturesLocal(designsCaptures, fetchedCapturedSides)
                await dispatch(actionCaptureBackground(result))
                debug("capture end", fetchedCapturedSides);
                if (isNext) {
                    if (designMode === DESIGN_MODES.COMBINED) {
                        await dispatch(actionSetRunningImageCombine(false))
                        return fetchedCapturedSides
                    }
                    return await handleMergeDesignCaptures(result, dispatch)  // ← Thêm dòng này để return data mới nhất
                }
                return null
            } else {
                if (isNext) {
                    if (designMode === DESIGN_MODES.COMBINED) {
                        return await dispatch(actionSetRunningImageCombine(false))
                    }
                    return await handleMergeDesignCaptures(designsCaptures, dispatch)  // ← Thêm dòng này để return data mới nhất
                }
                return null
            }
        } catch (error) {
            setLocalData('status_capture_background', 'catured_error')
            console.error(`Error capturing individual sides: ${error.message}`)
        } finally {
            const numCaptureEnd = getLocalData('numCaptureEnd', 0)
            setLocalData('numCaptureEnd', numCaptureEnd + 1)
        }
    }, [designsCaptures, designMode])

    // Helper function để xử lý từng side riêng biệt
    const processIndividualSides = async (artworks, attribute) => {
        // Filter chỉ lấy sides có artwork
        const validArtworks = artworks.filter(artwork => artwork?.src)
        
        if (validArtworks.length === 0) return []
        
        // Get presigned URLs cho từng side
        const {data} = await getLinksUploadArtwork({limit: validArtworks.length})
        const presignedUrls = data || []
        
        if (presignedUrls.length !== validArtworks.length) {
            throw new Error('Failed to get presigned urls for individual sides')
        }
        
        // Convert blob URLs to actual blobs
        const sidesData = await Promise.all(
            validArtworks.map(async (artwork, index) => {
                const {src: blobUrl, side} = artwork
                const presignedUrl = presignedUrls[index]

                // Convert blob URL to actual blob
                const response = await fetch(blobUrl)
                const blob = await response.blob()
                
                return {
                    blob,
                    presignedUrl,
                    side
                }
            })
        )
        
        // Upload using worker (non-blocking)
        const uploadResults = await captureUpload(sidesData, {useWorker: true})
        
        // Validate upload results
        if (!Array.isArray(uploadResults)) {
            console.error('❌ Upload results is not an array:', uploadResults)
            throw new Error('Invalid upload results format')
        }
        
        if (uploadResults.length !== sidesData.length) {
            console.error('❌ Upload results length mismatch:', {
                expected: sidesData.length,
                received: uploadResults.length
            })
            throw new Error(`Expected ${sidesData.length} results, got ${uploadResults.length}`)
        }
        
        // Transform results to expected format
        const individualSides = uploadResults.map((result, index) => {
            // Handle different result formats
            const side = result?.side || sidesData[index]?.side
            const url = result?.url
            const success = result?.success
            const {blob} = sidesData[index]

            if (!success || !url) {
                console.error(`❌ Upload failed for ${side}:`, {
                    result,
                    success,
                    url,
                    fullResult: JSON.stringify(result)
                })
                throw new Error(`Failed to upload side: ${side}`)
            }
            
            return {
                side,
                url,
                blob, // Keep for backward compatibility
                metadata: {
                    size: blob.size,
                    type: blob.type,
                    uploadedAt: new Date().toISOString()
                }
            }
        })
        
        return individualSides
    }

    return {fetchAreas, fetchDesigns, fetchCapturedSides, percentage, pending, fetchPreviews, fetchPreview3D, fetchDesignsNoCapture}
}

